import type { FC, PropsWithChildren } from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

interface OpenStateValue {
    isOpen: boolean;
    open: () => void;
    close: () => void;
}

const OpenStateContext = createContext<OpenStateValue | undefined>(undefined);

// The slideout's open state is lifted above AltDealStructureGridContainer so the grid can gate its AI
// fetch on "slideout open" (refresh-only, like the other AI indicators) instead of fetching eagerly at
// mount. The container reads `isOpen` to drive the fetch; the slideout provider reads/sets it to show
// or hide the panel.
export const AiAltDealStructureOpenStateProvider: FC<PropsWithChildren> = ({ children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const open = useCallback(() => setIsOpen(true), []);
    const close = useCallback(() => setIsOpen(false), []);
    const value = useMemo<OpenStateValue>(() => ({ isOpen, open, close }), [isOpen, open, close]);

    return <OpenStateContext.Provider value={value}>{children}</OpenStateContext.Provider>;
};

export const useAiAltDealStructureOpenState = (): OpenStateValue => {
    const value = useContext(OpenStateContext);
    if (!value) {
        throw new Error('useAiAltDealStructureOpenState must be used within an AiAltDealStructureOpenStateProvider');
    }
    return value;
};
